import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Shield, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-[#0a0a0f] pt-20 pb-12 px-4" data-testid="privacy-page">
      <div className="max-w-4xl mx-auto">
        <Link to="/">
          <Button variant="ghost" className="mb-6 text-gray-400 hover:text-white">
            <ArrowLeft className="h-4 w-4 mr-2" /> Back to Home
          </Button>
        </Link>
        
        <Card className="bg-[#14120f] border-[#1c1814]">
          <CardHeader className="border-b border-[#1c1814]">
            <div className="flex items-center gap-3">
              <Shield className="h-8 w-8 text-[#D4AF37]" />
              <div>
                <CardTitle className="text-2xl text-white">Privacy Policy</CardTitle>
                <p className="text-gray-400 text-sm">Last updated: January 2025</p>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-6 prose prose-invert max-w-none">
            <div className="space-y-6 text-gray-300">
              <section>
                <h2 className="text-xl font-bold text-white mb-3">1. Information We Collect</h2>
                <p>We collect information you provide directly to us, including:</p>
                <ul className="list-disc pl-6 mt-2 space-y-1">
                  <li><strong>Account Information:</strong> Name, email address, and profile picture (via Google OAuth)</li>
                  <li><strong>Trading Data:</strong> Paper trading activity, watchlists, and portfolio information</li>
                  <li><strong>Usage Data:</strong> How you interact with our platform, courses completed, and features used</li>
                  <li><strong>Communication Data:</strong> Messages sent through our platform, support requests</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-bold text-white mb-3">2. How We Use Your Information</h2>
                <p>We use the information we collect to:</p>
                <ul className="list-disc pl-6 mt-2 space-y-1">
                  <li>Provide and improve our educational services</li>
                  <li>Personalize your learning experience</li>
                  <li>Process transactions and send related information</li>
                  <li>Send educational updates and marketing communications (with your consent)</li>
                  <li>Respond to your comments and questions</li>
                  <li>Monitor and analyze usage patterns</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-bold text-white mb-3">3. Information Sharing</h2>
                <p>We do not sell your personal information. We may share information with:</p>
                <ul className="list-disc pl-6 mt-2 space-y-1">
                  <li><strong>Service Providers:</strong> Third parties that help us operate our platform (hosting, analytics)</li>
                  <li><strong>Broker Integrations:</strong> When you connect to Alpaca, your API keys are stored securely to facilitate trading</li>
                  <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-bold text-white mb-3">4. Data Security</h2>
                <p>
                  We implement appropriate security measures to protect your personal information. However, no method of transmission over the Internet is 100% secure. We cannot guarantee absolute security of your data.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-bold text-white mb-3">5. Third-Party Services</h2>
                <p>Our platform integrates with third-party services including:</p>
                <ul className="list-disc pl-6 mt-2 space-y-1">
                  <li><strong>Google OAuth:</strong> For authentication</li>
                  <li><strong>Alpaca:</strong> For brokerage services (your Alpaca credentials are encrypted)</li>
                  <li><strong>TradingView:</strong> For charts and market data</li>
                </ul>
                <p className="mt-2">Each service has its own privacy policy that governs their use of your data.</p>
              </section>

              <section>
                <h2 className="text-xl font-bold text-white mb-3">6. Cookies and Tracking</h2>
                <p>
                  We use cookies and similar technologies to enhance your experience, analyze usage, and assist in our marketing efforts. You can control cookie settings through your browser.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-bold text-white mb-3">7. Your Rights</h2>
                <p>You have the right to:</p>
                <ul className="list-disc pl-6 mt-2 space-y-1">
                  <li>Access and receive a copy of your personal data</li>
                  <li>Request correction of inaccurate data</li>
                  <li>Request deletion of your data</li>
                  <li>Object to processing of your data</li>
                  <li>Withdraw consent at any time</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-bold text-white mb-3">8. Data Retention</h2>
                <p>
                  We retain your information for as long as your account is active or as needed to provide services. We may retain certain information as required by law or for legitimate business purposes.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-bold text-white mb-3">9. Children's Privacy</h2>
                <p>
                  Our Service is not intended for individuals under 18 years of age. We do not knowingly collect personal information from children under 18.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-bold text-white mb-3">10. Changes to This Policy</h2>
                <p>
                  We may update this privacy policy from time to time. We will notify you of any changes by posting the new policy on this page and updating the "Last updated" date.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-bold text-white mb-3">11. Contact Us</h2>
                <p>
                  If you have questions about this Privacy Policy, please contact us at: privacy@nouveaurichelegacy.com
                </p>
              </section>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
